// Ported 1:1 from app/templates/components/visualization/score-ring.js.html (React migration Pass 8).
// Same imperative API; CSS lives in app/static/css/components/*.
//
// ScoreRing - Whoop-inspired circular progress indicator
//
// Renders a circular ring that fills proportionally to a score.
// Supports multiple variants with different color thresholds.
//
// Usage:
//   ScoreRing.render('container-id', 0.85);
//   ScoreRing.render('container-id', 0.56, { variant: 'pressing', size: 'sm' });
//   ScoreRing.render('container-id', 0.42, { variant: 'dynamic', thresholds: { good: 0.50, okay: 0.35 } });
//   ScoreRing.update('container-id', 0.72);
//
// Options:
//   variant: 'default' | 'pressing' | 'performance' | 'dynamic' - color threshold rules
//   size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' - ring size (default: 'md')
//   showValue: boolean - show numeric value (default: true)
//   thresholds: { good: number, okay: number } - custom thresholds (for 'dynamic' variant only)
//
// Dynamic Variant:
//   For percentile-based coloring relative to actual data distribution.
//   Pass thresholds calculated from your data (e.g., 33rd/66th percentiles).
//   Example: { good: 0.50, okay: 0.35 } means >= 50 is teal, >= 35 is amber, < 35 is coral.
//
// Performance colors: Add .use-performance-colors to parent for teal/amber/coral palette
//
// CSS: Requires /static/css/components/score-ring.css

export interface ScoreThresholds {
  good: number;
  okay: number;
}

export interface ScoreRingOptions {
  variant?: string;
  size?: string;
  showValue?: boolean;
  thresholds?: ScoreThresholds;
}

export interface ScoreRingResult {
  score: number | null;
  color: string | null;
  displayValue: number | string;
  scoreLevel: string;
}

interface Variant {
  getColor: (score: number, thresholds?: ScoreThresholds) => string;
  minScore: number;
}

interface SizeConfig {
  size: number;
  radius: number;
  stroke: number;
}

// Legacy design tokens (match design-tokens.css --color-priori-*)
const COLORS = {
  good: '#65BA74', // --color-priori-good
  okay: '#FFD656', // --color-priori-okay
  bad: '#DC2626' // --color-priori-bad
};

// Performance color tokens (match design-tokens.css --color-performance-*)
const PERFORMANCE_COLORS = {
  good: '#2BD9B0', // --color-performance-good (teal)
  goodDeep: '#1A9980', // --color-performance-good-deep
  okay: '#F4B942', // --color-performance-okay (warm amber)
  okayDeep: '#CC8F1A', // --color-performance-okay-deep
  bad: '#E25555', // --color-performance-bad (muted coral)
  badDeep: '#B52D2D' // --color-performance-bad-deep
};

// Centralized threshold definitions (used by ScoreRing and MiniBarChart)
// `satisfies` rather than `: Record<string, …>` — the annotation widened
// the key type to `string`, so THRESHOLDS.default was possibly undefined
// three lines below where it is defined. This keeps the shape checked and
// the literal keys known.
const THRESHOLDS = {
  default: { good: 0.8, okay: 0.5 }, // Standard performance (≥80 green, ≥50 yellow)
  performance: { good: 0.8, okay: 0.5 }, // Alias for default
  pressing: { good: 0.6, okay: 0.54 }, // The pressing index (the match page's pressing indicator, a legacy game's Defending ring)
  passes: { good: 0.5, okay: 0.35 } // Progressive passes (unused)
} satisfies Record<string, ScoreThresholds>;

// Get score level (good/okay/bad) based on variant thresholds
// For dynamic variant, pass thresholds as third argument
function getScoreLevel(score: number, variantName: string, thresholds?: ScoreThresholds): string {
  if (variantName === 'dynamic' && thresholds) {
    if (score >= thresholds.good) return 'good';
    if (score >= thresholds.okay) return 'okay';
    return 'bad';
  }
  const t = THRESHOLDS[variantName as keyof typeof THRESHOLDS] ?? THRESHOLDS.default;
  if (score >= t.good) return 'good';
  if (score >= t.okay) return 'okay';
  return 'bad';
}

// Variant configurations
/** The fallback, named once. Every lookup falls back to it, and naming it
 *  means the compiler knows the result of that fallback is a Variant rather
 *  than another possibly-missing key. */
const VARIANTS: Record<string, Variant> = {
  default: {
    getColor: (score) => {
      const t = THRESHOLDS.default;
      if (score >= t.good) return COLORS.good;
      if (score >= t.okay) return COLORS.okay;
      return COLORS.bad;
    },
    minScore: 0
  },
  pressing: {
    getColor: (score) => {
      const t = THRESHOLDS.pressing;
      if (score >= t.good) return COLORS.good;
      if (score >= t.okay) return COLORS.okay;
      return COLORS.bad;
    },
    minScore: 0
  },
  performance: {
    // Alias for default - same thresholds
    getColor: (score) => {
      const t = THRESHOLDS.default;
      if (score >= t.good) return COLORS.good;
      if (score >= t.okay) return COLORS.okay;
      return COLORS.bad;
    },
    minScore: 0
  },
  passes: {
    // Progressive passes: calibrated to score range 0.11-0.84, avg ~0.45
    getColor: (score) => {
      const t = THRESHOLDS.passes;
      if (score >= t.good) return COLORS.good;
      if (score >= t.okay) return COLORS.okay;
      return COLORS.bad;
    },
    minScore: 0
  },
  // Dynamic variant: accepts custom thresholds via options
  // options.thresholds = { good: 0.66, okay: 0.33 }
  dynamic: {
    getColor: (score, thresholds) => {
      const t = thresholds || { good: 0.66, okay: 0.33 };
      if (score >= t.good) return COLORS.good;
      if (score >= t.okay) return COLORS.okay;
      return COLORS.bad;
    },
    minScore: 0
  }
};

const DEFAULT_VARIANT: Variant = VARIANTS.default as Variant


// Size configurations (must match score-ring.css and metric-card.css)
// Formula: radius ≈ size/2 - stroke - padding, stroke ≈ size/12
/** The sizes that exist. A caller passing anything else falls back to 'md'
 *  at the one place the string enters, rather than every lookup site having
 *  to cope with a key that may not be there. */
type SizeKey = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const SIZES = {
  xs: { size: 40, radius: 15, stroke: 4 }, // popovers/tooltips
  sm: { size: 56, radius: 22, stroke: 5 }, // 144px card
  md: { size: 100, radius: 40, stroke: 8 }, // 200px card
  lg: { size: 160, radius: 62, stroke: 14 }, // 280px card - tighter, heavier ring
  xl: { size: 200, radius: 80, stroke: 16 } // standalone hero
} satisfies Record<string, SizeConfig>;

function render(containerId: string, score: number, options: ScoreRingOptions = {}): ScoreRingResult | null {
  const container = document.getElementById(containerId);
  if (!container) return null;

  const variantName = options.variant || 'default';
  const variant = VARIANTS[variantName] ?? DEFAULT_VARIANT;
  const sizeKey: SizeKey = (options.size as SizeKey) in SIZES
    ? (options.size as SizeKey) : 'md';
  // Note: the original also destructured a `capStroke` key here; no size defines
  // it and nothing reads it, so it is dropped in the port.
  const { size, radius, stroke } = SIZES[sizeKey];
  const showValue = options.showValue !== false;

  // Check minimum threshold
  // For dynamic variant, pass thresholds to getColor
  const color =
    variantName === 'dynamic' ? variant.getColor(score, options.thresholds) : variant.getColor(score);
  if (!color) {
    container.style.display = 'none';
    return null;
  }

  // Calculate ring fill
  const circumference = 2 * Math.PI * radius;
  const filled = circumference * Math.min(score, 1);
  const displayValue = Math.round(score * 100);
  const center = size / 2;

  // Determine score level for CSS class
  const scoreLevel = getScoreLevel(score, variantName, options.thresholds);

  // Apply size class
  container.className = `score-ring score-ring--${sizeKey}`;
  container.style.display = '';

  container.innerHTML = `
            <svg viewBox="0 0 ${size} ${size}">
                <circle class="score-ring__track" cx="${center}" cy="${center}" r="${radius}" stroke-width="${stroke}"/>
                <circle class="score-ring__fill score-ring__fill--${scoreLevel}" cx="${center}" cy="${center}" r="${radius}" stroke-width="${stroke}"
                    stroke="${color}" stroke-dasharray="${filled} ${circumference}"/>
            </svg>
            ${showValue ? `<span class="score-ring__value">${displayValue}</span>` : ''}`;

  return { score, color, displayValue, scoreLevel };
}

function update(containerId: string, score: number, options: ScoreRingOptions = {}): ScoreRingResult | null {
  return render(containerId, score, options);
}

function getColor(score: number, variantName = 'default'): string {
  const variant = VARIANTS[variantName] ?? DEFAULT_VARIANT;
  return variant.getColor(score);
}

function getLevel(score: number, variantName = 'default'): string {
  return getScoreLevel(score, variantName);
}

/**
 * Render empty/N/A state when no data is available
 */
function renderEmpty(containerId: string, options: ScoreRingOptions = {}): ScoreRingResult | null {
  const container = document.getElementById(containerId);
  if (!container) return null;

  const sizeKey: SizeKey = (options.size as SizeKey) in SIZES
    ? (options.size as SizeKey) : 'md';
  const { size, radius, stroke } = SIZES[sizeKey];
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;

  container.className = `score-ring score-ring--${sizeKey} score-ring--empty`;
  container.style.display = '';

  container.innerHTML = `
            <svg viewBox="0 0 ${size} ${size}">
                <circle class="score-ring__track" cx="${center}" cy="${center}" r="${radius}" stroke-width="${stroke}"/>
            </svg>
            <span class="score-ring__value score-ring__value--empty">N/A</span>`;

  // Unused in the empty render, but kept for parity with the original math.
  void circumference;

  return { score: null, color: null, displayValue: 'N/A', scoreLevel: 'empty' };
}

// Get thresholds for a variant (for use by other components like MiniBarChart)
function getThresholds(variantName = 'default'): ScoreThresholds {
  return THRESHOLDS[variantName as keyof typeof THRESHOLDS] ?? THRESHOLDS.default;
}

// Public API (matches the original window-global object)
export const ScoreRing = {
  render,
  update,
  renderEmpty,
  getColor,
  getLevel,
  getThresholds,
  COLORS,
  PERFORMANCE_COLORS,
  VARIANTS,
  THRESHOLDS
};
